import { Inline, Stack, Text, textColors, textVariants, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css, cx, resetButtonStyle } from '@eti/styles';
import { MinusCircle, PlusCircle } from '@phosphor-icons/react';
import { useController, useFormContext } from 'react-hook-form';
import { passengerFields, PASSENGERS_FORM_SECTION } from '../../constants/formFields';
import { MAX_CHILD_AGE, MIN_CHILD_AGE } from '../../constants/passengers';
import {
  changeValueWithOperand,
  isValidAdultSelection,
  isValidChildSelection,
  isValidInfantSelection,
} from '../../utils/passengers';

const rowStyles = css`
  border-top: 0.0625rem solid ${vars.colors.divider};
  font-size: 1rem;
  min-height: 3.25rem;
  padding: 1rem;

  &:last-child {
    border-bottom: 0.0625rem solid ${vars.colors.divider};
  }
`;

const controlWrapperStyles = css`
  display: flex;
  justify-content: space-between;
  padding-inline-end: 6.25rem;
  position: relative;
`;

const positioningStyles = css`
  align-items: center;
  height: 100%;
  inset-inline-end: 0;
  position: absolute;
  top: 0.0625rem;
`;

const getButtonStyles = (isDisabled = false) => css`
  color: ${isDisabled ? vars.colors.inputs.disabledBorder : vars.colors.links.default};
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  display: flex;
  position: relative;

  &::after {
    border-color: ${vars.colors.focusRing};
    border-radius: 50%;
    border-style: solid;
    border-width: 0.125rem;
    content: '';
    height: 1.75rem;
    left: -0.125rem;
    opacity: 0;
    position: absolute;
    top: -0.125rem;
    transition: opacity 0.2s ease-in-out;
    width: 1.75rem;
  }

  &:focus-visible::after {
    opacity: 1;
  }

  &:active {
    transform: scale(${isDisabled ? '1' : '0.9'});
  }
`;

const hiddenInputStyles = css`
  clip: rect(0 0 0 0);
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
  z-index: -1;
`;

const currentValueStyles = css`
  align-items: center;
  display: flex;
  font-size: 1.25rem;
  height: 100%;
  justify-content: center;
  width: 1.5rem;
`;

const iconStyles = css`
  color: currentcolor;
  height: 1.5rem;
  width: 1.5rem;
`;

export interface PassengerRowNewProps {
  constraints?: {
    maxNumberOfTravelers: number;
    isDisabled: boolean;
  };
  description?: string;
  fieldName: string;
  label: string;
  passengerIndex?: number;
  toggleConstraints?: {
    add: { isDisabled: boolean };
    subtract: { isDisabled: boolean };
  };
}

const PassengerRowNew = ({
  constraints,
  description,
  fieldName,
  label,
  passengerIndex,
  toggleConstraints,
}: PassengerRowNewProps) => {
  const { t } = useTranslation();
  const { control, watch, getValues, setValue } = useFormContext();
  const {
    field: { onChange, name },
  } = useController({ control, name: fieldName });
  const selectedValue = watch(name);
  const selectedPassengers = getValues(PASSENGERS_FORM_SECTION);

  const handleOnChange = (operand: 'add' | 'subtract') => () => {
    switch (fieldName) {
      case `${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN_AGES}`:
        if (typeof passengerIndex === 'number') {
          const newValue = changeValueWithOperand(selectedValue[passengerIndex], operand);
          const currentChildrenAges = selectedValue;

          if (newValue < MIN_CHILD_AGE) {
            currentChildrenAges[passengerIndex] = MAX_CHILD_AGE;
          } else if (newValue > MAX_CHILD_AGE) {
            currentChildrenAges[passengerIndex] = MIN_CHILD_AGE;
          } else {
            currentChildrenAges[passengerIndex] = newValue;
          }

          onChange(currentChildrenAges);
        }
        break;
      case `${PASSENGERS_FORM_SECTION}.${passengerFields.ADULTS}`: {
        const newValue = changeValueWithOperand(selectedValue, operand);
        const changeIsValid = isValidAdultSelection(newValue, selectedPassengers, constraints);
        if (changeIsValid) {
          const newAmountOfInfants = Math.min(newValue, selectedPassengers.infants);
          setValue(`${PASSENGERS_FORM_SECTION}.${passengerFields.INFANTS}`, newAmountOfInfants);
          onChange(newValue);
        }
        break;
      }
      case `${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN}`: {
        const newValue = changeValueWithOperand(selectedValue, operand);
        const changeIsValid = isValidChildSelection(newValue, selectedPassengers, constraints);
        if (changeIsValid) {
          onChange(newValue);
        }
        break;
      }
      case `${PASSENGERS_FORM_SECTION}.${passengerFields.INFANTS}`: {
        const newValue = changeValueWithOperand(selectedValue, operand);
        const changeIsValid = isValidInfantSelection(newValue, selectedPassengers);
        if (changeIsValid) {
          onChange(newValue);
        }
        break;
      }
      default: {
        onChange(changeValueWithOperand(selectedValue, operand));
      }
    }
  };

  const fieldId =
    name === `${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN_AGES}`
      ? `${name}-${passengerIndex! + 1}`
      : name;
  const inputId = `${fieldId}-passengers-input`;

  return (
    <Stack className={rowStyles}>
      <div className={controlWrapperStyles}>
        <label
          aria-describedby={description ? `passenger-${name}-description` : undefined}
          htmlFor={inputId}
        >
          {label}
        </label>
        <Inline className={positioningStyles} spacing={4}>
          <button
            aria-disabled={toggleConstraints?.subtract.isDisabled}
            aria-label={`${t('Air.Search.Subtract')} ${label}`}
            className={cx(
              resetButtonStyle,
              getButtonStyles(toggleConstraints?.subtract.isDisabled),
            )}
            data-testid={`${fieldId}-passengers-subtract`}
            disabled={toggleConstraints?.subtract.isDisabled}
            onClick={handleOnChange('subtract')}
            type="button"
          >
            <MinusCircle className={iconStyles} />
          </button>
          <input
            aria-hidden
            className={hiddenInputStyles}
            id={inputId}
            readOnly
            tabIndex={-1}
            type="number"
            value={
              fieldName === `${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN_AGES}`
                ? selectedValue[passengerIndex!]
                : selectedValue
            }
          />
          <div
            aria-hidden
            className={currentValueStyles}
            data-testid={`${fieldId}-passengers-currentValue`}
          >
            {fieldName === `${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN_AGES}`
              ? selectedValue[passengerIndex!]
              : selectedValue}
          </div>
          <button
            aria-disabled={toggleConstraints?.add.isDisabled}
            aria-label={`${t('Air.Search.Add')} ${label}`}
            className={cx(resetButtonStyle, getButtonStyles(toggleConstraints?.add.isDisabled))}
            data-testid={`${fieldId}-passengers-add`}
            disabled={toggleConstraints?.add.isDisabled}
            onClick={handleOnChange('add')}
            type="button"
          >
            <PlusCircle className={iconStyles} />
          </button>
        </Inline>
      </div>
      {description && (
        <Text
          as="p"
          color={textColors.SECONDARY}
          id={`passenger-${name}-description`}
          variant={textVariants._14}
        >
          {description}
        </Text>
      )}
    </Stack>
  );
};

export default PassengerRowNew;
